import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Mail, Plus, Loader2, Send, Trash2, Pencil, Clock, CheckCircle2,
  Bot, FileText, Eye, Copy, Code, GripVertical, Image, Type,
  Minus, Columns, ArrowUp, ArrowDown, X, Layout, Download, Upload,
  BarChart3, Save, Palette, Zap, Heart, RotateCcw, Settings, Sparkles
} from "lucide-react";
import { AgentChat } from "../components/AgentChat";
import { MailingSettings } from "../components/MailingSettings";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";
import { toast } from "sonner";
import { useSociete } from "../contexts/SocieteContext";
import { SegmentBuilder } from "../components/marketing/SegmentBuilder";
import { NEW_BLOCK_TYPES, DEFAULT_THEME, AiDesignPanel, ThemeEditor, MediaPicker, ImproveButton, PreviewToggle, DefaultStar } from "../components/marketing/MailDesignerParts";

const CAMPAIGN_STATUS_BG = {
  brouillon: "bg-slate-500/10 text-slate-400",
  planifiee: "bg-blue-500/10 text-blue-400",
  envoyee: "bg-green-500/10 text-green-400",
};

// ─── Pre-built template presets ───
const TEMPLATE_PRESETS = [
  {
    key: "newsletter",
    name: "Newsletter",
    description: "Newsletter classique avec articles et actualites",
    icon: "FileText",
    color: "#3b82f6",
    blocks: [
      { type: "header", data: { text: "Newsletter - Votre entreprise", level: "h1", align: "center", color: "#ffffff", bgColor: "#3b82f6", padding: "32px" } },
      { type: "text", data: { text: "Bonjour,\n\nDecouvrez nos dernieres actualites et conseils pour booster votre activite.", align: "left", color: "#333333", fontSize: "14px" } },
      { type: "divider", data: { color: "#e5e7eb", style: "solid", width: "1px" } },
      { type: "columns", data: { left: { text: "Article 1 : Titre de votre premier article. Resumez ici les points cles et les informations importantes pour vos lecteurs." }, right: { text: "Article 2 : Titre de votre deuxieme article. Partagez des conseils pratiques, des mises a jour ou des nouveautes." } } },
      { type: "divider", data: { color: "#e5e7eb", style: "solid", width: "1px" } },
      { type: "text", data: { text: "Ne manquez pas nos prochains evenements et formations. Inscrivez-vous des maintenant !", align: "center", color: "#555555", fontSize: "14px" } },
      { type: "button", data: { text: "Voir toutes les actualites", url: "#", color: "#ffffff", bgColor: "#3b82f6", align: "center", borderRadius: "6px", padding: "14px 28px" } },
      { type: "spacer", data: { height: "16px" } },
      { type: "text", data: { text: "Vous recevez cet email car vous etes inscrit a notre newsletter.\nPour vous desinscrire, cliquez ici.", align: "center", color: "#999999", fontSize: "11px" } },
    ],
  },
  {
    key: "promotion",
    name: "Promotion",
    description: "Offre promotionnelle avec visuel accrocheur",
    icon: "Zap",
    color: "#f59e0b",
    blocks: [
      { type: "header", data: { text: "OFFRE SPECIALE -20%", level: "h1", align: "center", color: "#ffffff", bgColor: "#f59e0b", padding: "32px" } },
      { type: "spacer", data: { height: "8px" } },
      { type: "image", data: { src: "", alt: "Visuel promotion", width: "100%", align: "center" } },
      { type: "text", data: { text: "Profitez de notre offre exceptionnelle !\n\nPendant une duree limitee, beneficiez de -20% sur l'ensemble de nos services d'accompagnement. C'est le moment ideal pour donner un coup d'accelerateur a votre entreprise.", align: "center", color: "#333333", fontSize: "16px" } },
      { type: "button", data: { text: "J'en profite maintenant", url: "#", color: "#ffffff", bgColor: "#f59e0b", align: "center", borderRadius: "30px", padding: "16px 36px" } },
      { type: "spacer", data: { height: "8px" } },
      { type: "text", data: { text: "Offre valable jusqu'au [date]. Conditions generales applicables.", align: "center", color: "#999999", fontSize: "11px" } },
    ],
  },
  {
    key: "bienvenue",
    name: "Bienvenue",
    description: "Email d'accueil pour nouveaux clients",
    icon: "Heart",
    color: "#10b981",
    blocks: [
      { type: "header", data: { text: "Bienvenue chez nous !", level: "h1", align: "center", color: "#ffffff", bgColor: "#10b981", padding: "32px" } },
      { type: "spacer", data: { height: "8px" } },
      { type: "text", data: { text: "Bonjour et bienvenue !\n\nNous sommes ravis de vous compter parmi nos clients. Votre confiance est notre plus grande motivation.\n\nVoici ce que vous pouvez faire des maintenant :", align: "left", color: "#333333", fontSize: "14px" } },
      { type: "columns", data: { left: { text: "Completez votre profil pour personnaliser votre experience et acceder a toutes les fonctionnalites." }, right: { text: "Explorez nos ressources et guides pour tirer le meilleur parti de nos services." } } },
      { type: "button", data: { text: "Decouvrir mon espace", url: "#", color: "#ffffff", bgColor: "#10b981", align: "center", borderRadius: "6px", padding: "14px 28px" } },
      { type: "spacer", data: { height: "8px" } },
      { type: "text", data: { text: "Une question ? Repondez directement a cet email, nous sommes la pour vous aider.", align: "center", color: "#777777", fontSize: "12px" } },
    ],
  },
  {
    key: "relance",
    name: "Relance",
    description: "Email de relance ou suivi client",
    icon: "RotateCcw",
    color: "#ef4444",
    blocks: [
      { type: "header", data: { text: "Vous nous manquez !", level: "h2", align: "center", color: "#ffffff", bgColor: "#64748b", padding: "24px" } },
      { type: "spacer", data: { height: "8px" } },
      { type: "text", data: { text: "Bonjour,\n\nNous avons remarque que vous n'avez pas donne suite a notre dernier echange. Nous restons a votre entiere disposition pour repondre a vos questions.\n\nPour rappel, voici ce que nous avions evoque :", align: "left", color: "#333333", fontSize: "14px" } },
      { type: "text", data: { text: "- Point 1 : Resumez ici le premier sujet aborde\n- Point 2 : Resumez ici le deuxieme point important\n- Point 3 : Prochaines etapes proposees", align: "left", color: "#555555", fontSize: "14px" } },
      { type: "button", data: { text: "Reprendre contact", url: "#", color: "#ffffff", bgColor: "#ef4444", align: "center", borderRadius: "6px", padding: "14px 28px" } },
      { type: "spacer", data: { height: "8px" } },
      { type: "text", data: { text: "Si vous n'etes plus interesse, aucun souci ! Nous restons disponibles pour l'avenir.", align: "center", color: "#999999", fontSize: "11px" } },
    ],
  },
];

// ─── Block types for the email editor ───
const BLOCK_TYPES = [
  { type: "header", label: "En-tete", icon: Layout, default: { text: "Titre de l'email", level: "h1", align: "center", color: "#ffffff", bgColor: "#10b981", padding: "24px" } },
  { type: "text", label: "Texte", icon: Type, default: { text: "Votre texte ici...", align: "left", color: "#333333", fontSize: "14px" } },
  { type: "image", label: "Image", icon: Image, default: { src: "", alt: "Image", width: "100%", align: "center" } },
  { type: "button", label: "Bouton CTA", icon: CheckCircle2, default: { text: "En savoir plus", url: "#", color: "#ffffff", bgColor: "#10b981", align: "center", borderRadius: "6px", padding: "12px 24px" } },
  { type: "divider", label: "Separateur", icon: Minus, default: { color: "#e5e7eb", style: "solid", width: "1px" } },
  { type: "spacer", label: "Espacement", icon: GripVertical, default: { height: "24px" } },
  { type: "columns", label: "2 Colonnes", icon: Columns, default: { left: { text: "Colonne gauche" }, right: { text: "Colonne droite" } } },
  ...NEW_BLOCK_TYPES.map((b) => ({ ...b, icon: Layout })),
];

let CURRENT_THEME = { ...DEFAULT_THEME };
let EDITOR_CTX = { api: null, societeId: null };
const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// ─── Block Renderer (edit mode) ───
function BlockEditor({ block, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [expanded, setExpanded] = useState(false);

  const updateData = (key, value) => {
    onChange(index, { ...block, data: { ...block.data, [key]: value } });
  };

  const renderPreview = () => {
    const d = block.data;
    switch (block.type) {
      case "hero":
        return <div style={{ background: d.bgColor || CURRENT_THEME.secondary }} className="p-3 text-center">{d.src && <img src={d.src} alt="" className="w-full h-16 object-cover rounded mb-2" />}<div className="text-white font-bold text-sm">{d.title}</div><div className="text-white/70 text-xs">{d.subtitle}</div>{d.cta && <span className="inline-block mt-2 px-3 py-1 rounded-full text-[10px] font-bold" style={{ background: CURRENT_THEME.primary, color: CURRENT_THEME.secondary }}>{d.cta}</span>}</div>;
      case "features":
        return <div className="p-2"><div className="text-xs font-bold text-white text-center mb-1">{d.title}</div><div className="grid grid-cols-3 gap-1">{(d.items || []).map((it, i) => <div key={i} className="text-center text-[10px] text-slate-300"><b>{it.title}</b><div className="text-slate-500">{it.text}</div></div>)}</div></div>;
      case "testimonial":
        return <div className="p-2 text-xs italic text-slate-300 border-l-2" style={{ borderColor: CURRENT_THEME.primary }}>« {d.quote} »<div className="text-[10px] text-slate-500 not-italic mt-1">— {d.author}{d.role ? `, ${d.role}` : ""}</div></div>;
      case "footer":
        return <div className="p-2 text-center text-[10px] text-slate-400"><b>{d.company}</b> · {d.address}<div>{d.legal}</div><div className="underline">Se désabonner</div></div>;
      case "header":
        return (
          <div style={{ backgroundColor: d.bgColor, padding: d.padding, textAlign: d.align }}>
            <span style={{ color: d.color, fontWeight: "bold", fontSize: d.level === "h1" ? "24px" : d.level === "h2" ? "20px" : "16px" }}>
              {d.text}
            </span>
          </div>
        );
      case "text":
        return <p style={{ textAlign: d.align, color: d.color, fontSize: d.fontSize }} className="px-2 py-1">{d.text}</p>;
      case "image":
        return d.src ? <img src={d.src} alt={d.alt} style={{ width: d.width, display: "block", margin: d.align === "center" ? "0 auto" : undefined }} /> :
          <div className="h-20 bg-slate-700/30 rounded flex items-center justify-center text-slate-500 text-xs"><Image className="w-5 h-5 mr-1" /> Ajouter une image</div>;
      case "button":
        return (
          <div style={{ textAlign: d.align, padding: "8px" }}>
            <span style={{ backgroundColor: d.bgColor, color: d.color, padding: d.padding, borderRadius: d.borderRadius, display: "inline-block", fontSize: "14px", fontWeight: "bold" }}>
              {d.text}
            </span>
          </div>
        );
      case "divider":
        return <hr style={{ borderColor: d.color, borderStyle: d.style, borderWidth: d.width, margin: "8px 0" }} />;
      case "spacer":
        return <div style={{ height: d.height }} className="bg-slate-800/20" />;
      case "columns":
        return (
          <div className="grid grid-cols-2 gap-2 p-2">
            <div className="bg-slate-800/30 rounded p-2 text-xs text-slate-400">{d.left?.text}</div>
            <div className="bg-slate-800/30 rounded p-2 text-xs text-slate-400">{d.right?.text}</div>
          </div>
        );
      default:
        return <div className="text-xs text-slate-500">Bloc inconnu</div>;
    }
  };

  const renderEditor = () => {
    const d = block.data;
    switch (block.type) {
      case "header":
        return (
          <div className="space-y-2">
            <Input value={d.text} onChange={e => updateData("text", e.target.value)} placeholder="Titre" className="bg-[#0b0811] border-white/10 text-white text-xs" />
            <div className="flex gap-2">
              <select value={d.level} onChange={e => updateData("level", e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs flex-1">
                <option value="h1">H1</option><option value="h2">H2</option><option value="h3">H3</option>
              </select>
              <select value={d.align} onChange={e => updateData("align", e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs flex-1">
                <option value="left">Gauche</option><option value="center">Centre</option><option value="right">Droite</option>
              </select>
            </div>
            <div className="flex gap-2">
              <label className="text-[10px] text-slate-500 flex items-center gap-1">Texte <input type="color" value={d.color} onChange={e => updateData("color", e.target.value)} className="w-6 h-6 rounded cursor-pointer" /></label>
              <label className="text-[10px] text-slate-500 flex items-center gap-1">Fond <input type="color" value={d.bgColor} onChange={e => updateData("bgColor", e.target.value)} className="w-6 h-6 rounded cursor-pointer" /></label>
            </div>
          </div>
        );
      case "text":
        return (
          <div className="space-y-2">
            <textarea value={d.text} onChange={e => updateData("text", e.target.value)} className="w-full bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs h-20 resize-none" />
            <div className="flex justify-end">{EDITOR_CTX.api && <ImproveButton api={EDITOR_CTX.api} text={d.text} onText={(t) => updateData("text", t)} />}</div>
            <div className="flex gap-2">
              <select value={d.align} onChange={e => updateData("align", e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs flex-1">
                <option value="left">Gauche</option><option value="center">Centre</option><option value="right">Droite</option>
              </select>
              <label className="text-[10px] text-slate-500 flex items-center gap-1">Couleur <input type="color" value={d.color} onChange={e => updateData("color", e.target.value)} className="w-6 h-6 rounded cursor-pointer" /></label>
              <select value={d.fontSize} onChange={e => updateData("fontSize", e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs">
                <option value="12px">12px</option><option value="14px">14px</option><option value="16px">16px</option><option value="18px">18px</option>
              </select>
            </div>
          </div>
        );
      case "image":
        return (
          <div className="space-y-2">
            {EDITOR_CTX.api && <MediaPicker api={EDITOR_CTX.api} societeId={EDITOR_CTX.societeId} value={d.src} onChange={(v) => updateData("src", v)} />}
            <Input value={d.src} onChange={e => updateData("src", e.target.value)} placeholder="URL de l'image" className="bg-[#0b0811] border-white/10 text-white text-xs" />
            <Input value={d.alt} onChange={e => updateData("alt", e.target.value)} placeholder="Texte alternatif" className="bg-[#0b0811] border-white/10 text-white text-xs" />
            <select value={d.width} onChange={e => updateData("width", e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs">
              <option value="100%">Pleine largeur</option><option value="75%">75%</option><option value="50%">50%</option>
            </select>
          </div>
        );
      case "button":
        return (
          <div className="space-y-2">
            <Input value={d.text} onChange={e => updateData("text", e.target.value)} placeholder="Texte du bouton" className="bg-[#0b0811] border-white/10 text-white text-xs" />
            <Input value={d.url} onChange={e => updateData("url", e.target.value)} placeholder="URL du lien" className="bg-[#0b0811] border-white/10 text-white text-xs" />
            <div className="flex gap-2">
              <select value={d.align} onChange={e => updateData("align", e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs flex-1">
                <option value="left">Gauche</option><option value="center">Centre</option><option value="right">Droite</option>
              </select>
              <label className="text-[10px] text-slate-500 flex items-center gap-1">Texte <input type="color" value={d.color} onChange={e => updateData("color", e.target.value)} className="w-6 h-6 rounded cursor-pointer" /></label>
              <label className="text-[10px] text-slate-500 flex items-center gap-1">Fond <input type="color" value={d.bgColor} onChange={e => updateData("bgColor", e.target.value)} className="w-6 h-6 rounded cursor-pointer" /></label>
            </div>
          </div>
        );
      case "divider":
        return (
          <div className="flex gap-2">
            <label className="text-[10px] text-slate-500 flex items-center gap-1">Couleur <input type="color" value={d.color} onChange={e => updateData("color", e.target.value)} className="w-6 h-6 rounded cursor-pointer" /></label>
            <select value={d.style} onChange={e => updateData("style", e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs">
              <option value="solid">Solide</option><option value="dashed">Tirets</option><option value="dotted">Points</option>
            </select>
          </div>
        );
      case "spacer":
        return (
          <select value={d.height} onChange={e => updateData("height", e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs">
            <option value="8px">8px</option><option value="16px">16px</option><option value="24px">24px</option><option value="32px">32px</option><option value="48px">48px</option>
          </select>
        );
      case "columns":
        return (
          <div className="grid grid-cols-2 gap-2">
            <textarea value={d.left?.text || ""} onChange={e => updateData("left", { ...d.left, text: e.target.value })}
              className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs h-16 resize-none" placeholder="Colonne gauche" />
            <textarea value={d.right?.text || ""} onChange={e => updateData("right", { ...d.right, text: e.target.value })}
              className="bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs h-16 resize-none" placeholder="Colonne droite" />
          </div>
        );
      case "hero":
        return (
          <div className="space-y-2">
            <input value={d.title || ""} onChange={e => updateData("title", e.target.value)} placeholder="Titre" className="w-full bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" data-testid="hero-title" />
            <textarea value={d.subtitle || ""} onChange={e => updateData("subtitle", e.target.value)} placeholder="Sous-titre" className="w-full bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs h-14 resize-none" />
            <div className="flex gap-2"><input value={d.cta || ""} onChange={e => updateData("cta", e.target.value)} placeholder="Texte du bouton" className="flex-1 bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" />
              <input value={d.url || ""} onChange={e => updateData("url", e.target.value)} placeholder="https://…" className="flex-1 bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" /></div>
            {EDITOR_CTX.api && <MediaPicker api={EDITOR_CTX.api} societeId={EDITOR_CTX.societeId} value={d.src} onChange={(v) => updateData("src", v)} />}
            <div className="flex justify-end">{EDITOR_CTX.api && <ImproveButton api={EDITOR_CTX.api} text={d.subtitle} onText={(t) => updateData("subtitle", t)} />}</div>
          </div>
        );
      case "features":
        return (
          <div className="space-y-2">
            <input value={d.title || ""} onChange={e => updateData("title", e.target.value)} placeholder="Titre de la section" className="w-full bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" />
            {(d.items || []).map((it, i) => (
              <div key={i} className="flex gap-2">
                <input value={it.title || ""} onChange={e => updateData("items", d.items.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder={`Avantage ${i + 1}`} className="w-1/3 bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" />
                <input value={it.text || ""} onChange={e => updateData("items", d.items.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} placeholder="Détail" className="flex-1 bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" />
              </div>
            ))}
          </div>
        );
      case "testimonial":
        return (
          <div className="space-y-2">
            <textarea value={d.quote || ""} onChange={e => updateData("quote", e.target.value)} placeholder="Citation" className="w-full bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs h-16 resize-none" />
            <div className="flex gap-2"><input value={d.author || ""} onChange={e => updateData("author", e.target.value)} placeholder="Auteur" className="flex-1 bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" />
              <input value={d.role || ""} onChange={e => updateData("role", e.target.value)} placeholder="Fonction, société" className="flex-1 bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" /></div>
          </div>
        );
      case "footer":
        return (
          <div className="space-y-2">
            <input value={d.company || ""} onChange={e => updateData("company", e.target.value)} placeholder="Société" className="w-full bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" />
            <input value={d.address || ""} onChange={e => updateData("address", e.target.value)} placeholder="Adresse" className="w-full bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs" />
            <textarea value={d.legal || ""} onChange={e => updateData("legal", e.target.value)} placeholder="Mentions légales" className="w-full bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1 text-xs h-14 resize-none" />
            <div className="text-[10px] text-slate-500">Le lien « Se désabonner » est ajouté automatiquement.</div>
          </div>
        );
      default:
        return null;
    }
  };

  const blockType = BLOCK_TYPES.find(b => b.type === block.type);
  const Icon = blockType?.icon || Type;

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-lg overflow-hidden hover:border-white/10 transition-colors" data-testid={`block-${index}`}>
      {/* Block header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-[#0b0811]/50">
        <GripVertical className="w-3 h-3 text-slate-600 cursor-grab" />
        <Icon className="w-3 h-3 text-slate-400" />
        <span className="text-[10px] text-slate-400 font-medium flex-1">{blockType?.label || block.type}</span>
        <div className="flex gap-0.5">
          {!isFirst && <button onClick={() => onMoveUp(index)} className="p-1 hover:bg-white/5 rounded"><ArrowUp className="w-3 h-3 text-slate-500" /></button>}
          {!isLast && <button onClick={() => onMoveDown(index)} className="p-1 hover:bg-white/5 rounded"><ArrowDown className="w-3 h-3 text-slate-500" /></button>}
          <button onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-white/5 rounded">
            <Pencil className="w-3 h-3 text-slate-500" />
          </button>
          <button onClick={() => onRemove(index)} className="p-1 hover:bg-red-500/10 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white/[0.02] p-2 text-white">{renderPreview()}</div>

      {/* Editor (expandable) */}
      {expanded && (
        <div className="p-3 border-t border-white/5">{renderEditor()}</div>
      )}
    </div>
  );
}

// ─── HTML Generator from blocks ───
function blocksToHtml(blocks, theme) {
  const th = { ...DEFAULT_THEME, ...(theme || CURRENT_THEME) };
  let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:0;font-family:${th.font};background:${th.background}}.container{max-width:600px;margin:0 auto;background:${th.surface}}p{margin:0;line-height:1.6}@media(max-width:620px){.col{display:block!important;width:100%!important}}</style></head><body><div class="container">`;
  for (const block of blocks) {
    const d = block.data;
    switch (block.type) {
      case "hero":
        html += `<div style="background:${d.bgColor || th.secondary};text-align:center">${d.src ? `<img src="${d.src}" alt="${esc(d.alt)}" style="width:100%;display:block" />` : ""}<div style="padding:28px 24px"><h1 style="color:${d.color || "#fff"};margin:0 0 10px;font-size:26px">${esc(d.title)}</h1><p style="color:${d.color || "#fff"};opacity:.9;font-size:15px">${esc(d.subtitle)}</p>${d.cta ? `<a href="${d.url || "#"}" style="display:inline-block;margin-top:16px;background:${th.primary};color:${th.secondary};padding:12px 26px;border-radius:999px;text-decoration:none;font-weight:bold">${esc(d.cta)}</a>` : ""}</div></div>`;
        break;
      case "features": {
        const items = (d.items || []).slice(0, 3);
        html += `<div style="padding:8px 12px">${d.title ? `<h2 style="text-align:center;color:${th.text};font-size:18px;margin:8px 0 4px">${esc(d.title)}</h2>` : ""}<table width="100%" cellpadding="0" cellspacing="0"><tr>${items.map((i) => `<td class="col" style="padding:12px;vertical-align:top;text-align:center;width:${Math.floor(100 / Math.max(1, items.length))}%"><div style="width:36px;height:36px;border-radius:50%;background:${th.primary};margin:0 auto 8px"></div><b style="color:${th.text};font-size:14px">${esc(i.title)}</b><p style="color:#555;font-size:13px;margin-top:4px">${esc(i.text)}</p></td>`).join("")}</tr></table></div>`;
        break;
      }
      case "testimonial":
        html += `<div style="margin:12px 24px;padding:18px 20px;background:#faf7ee;border-left:4px solid ${th.primary};border-radius:6px"><p style="color:${th.text};font-size:15px;font-style:italic">« ${esc(d.quote)} »</p><p style="color:#666;font-size:13px;margin-top:8px">— ${esc(d.author)}${d.role ? ", " + esc(d.role) : ""}</p></div>`;
        break;
      case "footer":
        html += `<div style="background:${d.bgColor || "#f0f0f2"};padding:18px 24px;text-align:center;color:#777;font-size:11px;line-height:1.6"><b style="color:#444">${esc(d.company)}</b><br/>${esc(d.address)}<br/>${esc(d.legal)}<br/><a href="{{unsubscribe_url}}" style="color:#777">Se désabonner</a></div>`;
        break;
      case "header":
        html += `<div style="background-color:${d.bgColor};padding:${d.padding};text-align:${d.align}"><${d.level} style="color:${d.color};margin:0">${d.text}</${d.level}></div>`;
        break;
      case "text":
        html += `<div style="padding:8px 16px;text-align:${d.align}"><p style="color:${d.color};font-size:${d.fontSize};margin:0;line-height:1.6">${d.text}</p></div>`;
        break;
      case "image":
        if (d.src) html += `<div style="text-align:${d.align};padding:8px 0"><img src="${d.src}" alt="${d.alt}" style="width:${d.width};max-width:100%;display:inline-block" /></div>`;
        break;
      case "button":
        html += `<div style="text-align:${d.align};padding:16px"><a href="${d.url}" style="background-color:${d.bgColor};color:${d.color};padding:${d.padding};border-radius:${d.borderRadius};text-decoration:none;display:inline-block;font-weight:bold;font-size:14px">${d.text}</a></div>`;
        break;
      case "divider":
        html += `<hr style="border:none;border-top:${d.width} ${d.style} ${d.color};margin:16px" />`;
        break;
      case "spacer":
        html += `<div style="height:${d.height}"></div>`;
        break;
      case "columns":
        html += `<table width="100%" cellpadding="0" cellspacing="0"><tr><td width="50%" style="padding:8px 16px;vertical-align:top"><p style="margin:0;font-size:14px;color:#333">${d.left?.text || ""}</p></td><td width="50%" style="padding:8px 16px;vertical-align:top"><p style="margin:0;font-size:14px;color:#333">${d.right?.text || ""}</p></td></tr></table>`;
        break;
      default: break;
    }
  }
  html += `</div></body></html>`;
  return html;
}

// ─── Main MailingPage ───
export default function MailingPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [tab, setTab] = useState("campagnes");
  const [campaigns, setCampaigns] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAgent, setShowAgent] = useState(false);

  // Campaign dialog
  const [showCampaign, setShowCampaign] = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);
  const [campForm, setCampForm] = useState({ name: "", subject: "", content_html: "", recipients_type: "all_clients", recipients_tags: [], segment: {}, scheduled_at: "" });
  const [saving, setSaving] = useState(false);

  // Template editor
  const [showEditor, setShowEditor] = useState(false);
  const [editorBlocks, setEditorBlocks] = useState([]);
  const [editorName, setEditorName] = useState("");
  const [editorDesc, setEditorDesc] = useState("");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showHtmlImport, setShowHtmlImport] = useState(false);
  const [htmlImport, setHtmlImport] = useState("");
  const [editorTab, setEditorTab] = useState("visual");
  const [editorTheme, setEditorTheme] = useState({ ...DEFAULT_THEME });
  const [editorSubject, setEditorSubject] = useState("");
  const [previewMode, setPreviewMode] = useState("desktop");
  const { currentSociete, societes } = useSociete();
  const societeId = currentSociete?.societe_id || societes?.[0]?.societe_id;
  EDITOR_CTX = { api, societeId };
  CURRENT_THEME = editorTheme;
  const applyAiDesign = (d) => {
    setEditorTheme({ ...DEFAULT_THEME, ...(d.theme || {}) });
    setEditorBlocks((d.blocks || []).map((b) => (b.type === "header" ? { ...b, data: { ...b.data, text: b.data?.text || b.data?.title || "" } } : b)));
    setEditorSubject(d.subject || "");
    if (!editorName) setEditorName(d.subject || "Design IA");
  };
  const setDefaultTemplate = async (tid) => {
    try { await api.post(`/marketing/email-templates/${tid}/default`); toast.success("Modèle par défaut de l'agent éditorial"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, t] = await Promise.all([
        api.get("/marketing/campaigns"),
        api.get("/marketing/stats"),
        api.get("/marketing/email-templates").catch(() => ({ data: [] })),
      ]);
      setCampaigns(c.data || []);
      setStats(s.data);
      setEmailTemplates(t.data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, []);

  // ── Campaign handlers ──
  const saveCampaign = async () => {
    setSaving(true);
    try {
      if (editCampaign) {
        await api.put(`/marketing/campaigns/${editCampaign.campaign_id}`, { ...campForm, societe_id: societeId });
      } else {
        await api.post("/marketing/campaigns", { ...campForm, societe_id: societeId });
      }
      setShowCampaign(false);
      setEditCampaign(null);
      setCampForm({ name: "", subject: "", content_html: "", recipients_type: "all_clients", recipients_tags: [], segment: {}, scheduled_at: "" });
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const deleteCampaign = async (cid) => {
    if (!window.confirm("Supprimer cette campagne ?")) return;
    await api.delete(`/marketing/campaigns/${cid}`);
    reload();
  };

  const sendCampaign = async (cid) => {
    if (!window.confirm("Envoyer cette campagne ?")) return;
    try {
      const r = await api.post(`/marketing/campaigns/${cid}/send`);
      alert(r.data.message);
      reload();
    } catch { alert("Erreur envoi"); }
  };

  const openEditCampaign = (c) => {
    setEditCampaign(c);
    setCampForm({ name: c.name, subject: c.subject, content_html: c.content_html || "", recipients_type: c.recipients_type || "all_clients", recipients_tags: c.recipients_tags || [], segment: c.segment || {}, scheduled_at: c.scheduled_at || "" });
    setShowCampaign(true);
  };

  const openNewCampaign = () => {
    setEditCampaign(null);
    setCampForm({ name: "", subject: "", content_html: "", recipients_type: "all_clients", recipients_tags: [], segment: {}, scheduled_at: "" });
    setShowCampaign(true);
  };

  // ── Template editor handlers ──
  const [showPresetPicker, setShowPresetPicker] = useState(false);

  const openNewTemplate = () => {
    setShowPresetPicker(true);
  };

  const startFromPreset = (preset) => {
    setEditingTemplate(null);
    setEditorBlocks(preset.blocks.map(b => ({ ...b, data: { ...b.data } })));
    setEditorName(preset.name);
    setEditorDesc(preset.description);
    setEditorTab("visual");
    setShowPresetPicker(false);
    setShowEditor(true);
  };

  const startFromScratch = () => {
    setEditingTemplate(null);
    setEditorBlocks([
      { type: "header", data: { ...BLOCK_TYPES[0].default } },
      { type: "text", data: { ...BLOCK_TYPES[1].default } },
      { type: "button", data: { ...BLOCK_TYPES[3].default } },
    ]);
    setEditorName("");
    setEditorDesc("");
    setEditorTab("visual");
    setShowPresetPicker(false);
    setShowEditor(true);
  };

  const openEditTemplate = (tpl) => {
    setEditingTemplate(tpl);
    setEditorBlocks(tpl.blocks?.length > 0 ? tpl.blocks : [
      { type: "header", data: { ...BLOCK_TYPES[0].default } },
      { type: "text", data: { ...BLOCK_TYPES[1].default } },
    ]);
    setEditorName(tpl.name || "");
    setEditorDesc(tpl.description || "");
    setEditorTheme({ ...DEFAULT_THEME, ...(tpl.theme || {}) });
    setEditorSubject(tpl.subject || "");
    setEditorTab("visual");
    setShowEditor(true);
  };

  const addBlock = (type) => {
    const bt = BLOCK_TYPES.find(b => b.type === type);
    if (bt) setEditorBlocks(prev => [...prev, { type, data: { ...bt.default } }]);
  };

  const updateBlock = (idx, block) => {
    setEditorBlocks(prev => prev.map((b, i) => i === idx ? block : b));
  };

  const removeBlock = (idx) => {
    setEditorBlocks(prev => prev.filter((_, i) => i !== idx));
  };

  const moveBlock = (idx, dir) => {
    setEditorBlocks(prev => {
      const copy = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= copy.length) return copy;
      [copy[idx], copy[target]] = [copy[target], copy[idx]];
      return copy;
    });
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      const html = blocksToHtml(editorBlocks, editorTheme);
      const payload = { name: editorName, description: editorDesc, blocks: editorBlocks, html, theme: editorTheme, subject: editorSubject, societe_id: societeId };
      if (editingTemplate) {
        await api.put(`/marketing/email-templates/${editingTemplate.template_id}`, payload);
      } else {
        await api.post("/marketing/email-templates", payload);
      }
      setShowEditor(false);
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const deleteTemplate = async (tid) => {
    if (!window.confirm("Supprimer ce template ?")) return;
    await api.delete(`/marketing/email-templates/${tid}`);
    reload();
  };

  const previewTemplate = () => {
    setPreviewHtml(blocksToHtml(editorBlocks, editorTheme));
    setShowPreview(true);
  };

  const applyTemplateForCampaign = (tpl) => {
    setCampForm(f => ({ ...f, subject: f.subject || tpl.subject || "", content_html: tpl.html || blocksToHtml(tpl.blocks || [], tpl.theme) }));
    setShowCampaign(true);
  };

  const importHtml = () => {
    setCampForm(f => ({ ...f, content_html: htmlImport }));
    setShowHtmlImport(false);
    setHtmlImport("");
  };

  const tabs = [
    { key: "campagnes", label: "Campagnes", icon: Send },
    { key: "templates", label: "Templates", icon: Layout },
    { key: "parametres", label: "Parametres", icon: Settings },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="mailing-page">
      <PageHeader
        icon={<Mail />}
        title="Mailing"
        description="Campagnes · Templates · Tracking · A/B testing"
        testId="mailing-header"
        actions={(
          <>
            <Button
              onClick={() => setShowAgent(!showAgent)}
              variant="outline"
              className={`border-white/10 text-slate-300 hover:bg-white/5 ${showAgent ? "bg-[#10b981]/10 border-[#10b981]/30 text-[#10b981]" : ""}`}
              data-testid="mailing-agent-btn"
            >
              <Bot className="w-4 h-4 mr-1" /> Agent IA
            </Button>
            {tab === "campagnes" && (
              <Button onClick={openNewCampaign} className="bg-[#3b82f6] text-white hover:bg-[#2563eb] font-bold text-sm" data-testid="create-campaign-btn">
                <Plus className="w-4 h-4 mr-1" /> Campagne
              </Button>
            )}
            {tab === "templates" && (
              <Button onClick={openNewTemplate} className="bg-[#3b82f6] text-white hover:bg-[#2563eb] font-bold text-sm" data-testid="create-template-btn">
                <Plus className="w-4 h-4 mr-1" /> Template
              </Button>
            )}
          </>
        )}
      />

      {/* Agent IA Panel */}
      {showAgent && (
        <div className="mb-5">
          <AgentChat agentType="marketing" agentName="Agent Marketing" agentColor="#10b981" onClose={() => setShowAgent(false)} />
        </div>
      )}

      {/* Stats */}
      {stats?.campaigns && !showAgent && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
            <div className="text-[10px] text-slate-500 uppercase">Campagnes</div>
            <div className="text-xl font-black text-[#3b82f6]">{stats.campaigns.total}</div>
          </div>
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
            <div className="text-[10px] text-slate-500 uppercase">Emails envoyes</div>
            <div className="text-xl font-black text-[#10b981]">{stats.campaigns.total_sent}</div>
          </div>
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
            <div className="text-[10px] text-slate-500 uppercase">Taux ouverture</div>
            <div className="text-xl font-black text-[#f7c935]">{stats.campaigns.taux_ouverture}%</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {!showAgent && (
        <>
          <div className="flex gap-1 mb-5 border-b border-white/10 pb-3 overflow-x-auto">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`mailing-tab-${t.key}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  tab === t.key ? "bg-[#3b82f6] text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {loading && <ListSkeleton items={4} testId="mailing-loading-skeleton" />}

          {/* ── CAMPAGNES TAB ── */}
          {!loading && tab === "campagnes" && (
            <div data-testid="campaigns-list">
              {campaigns.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                  <Mail className="w-14 h-14 mx-auto mb-4 text-slate-600" />
                  <p>Aucune campagne mailing</p>
                  <p className="text-xs mt-1">Creez votre premiere campagne email</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {campaigns.map(c => (
                    <div key={c.campaign_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`campaign-${c.campaign_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-[#3b82f6]" />
                          <span className="text-sm font-bold">{c.name}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${CAMPAIGN_STATUS_BG[c.status] || CAMPAIGN_STATUS_BG.brouillon}`}>{c.status}</span>
                        </div>
                        <div className="flex gap-1">
                          {c.status === "brouillon" && (
                            <button onClick={() => sendCampaign(c.campaign_id)} className="p-1.5 hover:bg-green-500/10 rounded" title="Envoyer" data-testid={`send-campaign-${c.campaign_id}`}>
                              <Send className="w-3.5 h-3.5 text-green-400" />
                            </button>
                          )}
                          <button onClick={() => openEditCampaign(c)} className="p-1.5 hover:bg-white/5 rounded"><Pencil className="w-3 h-3 text-slate-400" /></button>
                          <button onClick={() => deleteCampaign(c.campaign_id)} className="p-1.5 hover:bg-red-500/10 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                        </div>
                      </div>
                      <div className="text-xs text-slate-400">Objet : {c.subject || "-"}</div>
                      <div className="flex gap-3 mt-2 text-[10px] text-slate-500">
                        <span>Destinataires : {c.recipients_type === "segment" ? `Segment ciblé${(c.segment?.tags || []).length ? " · tags " + c.segment.tags.join(", ") : ""}${(c.segment?.program_ids || []).length ? ` · ${c.segment.program_ids.length} programme(s)` : ""}${c.segment?.last_activity_days ? ` · actifs ${c.segment.last_activity_days} j` : ""}` : c.recipients_type === "tagged" ? `Tags: ${c.recipients_tags?.join(", ")}` : "Tous les clients"}</span>
                        {c.status === "envoyee" && <span className="text-green-400">{c.stats?.sent || 0} envoyes</span>}
                        {c.scheduled_at && <span><Clock className="w-3 h-3 inline mr-0.5" />{new Date(c.scheduled_at).toLocaleDateString("fr-FR")}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── TEMPLATES TAB ── */}
          {!loading && tab === "templates" && (
            <div data-testid="templates-list">
              {emailTemplates.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                  <Layout className="w-14 h-14 mx-auto mb-4 text-slate-600" />
                  <p>Aucun template email</p>
                  <p className="text-xs mt-1">Creez votre premier template avec l'editeur visuel</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {emailTemplates.map(tpl => (
                    <div key={tpl.template_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`template-${tpl.template_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold truncate">{tpl.name}</span>
                        <div className="flex gap-1">
                          <button onClick={() => openEditTemplate(tpl)} className="p-1.5 hover:bg-white/5 rounded" title="Modifier"><Pencil className="w-3 h-3 text-slate-400" /></button>
                          <DefaultStar isDefault={!!tpl.is_default} onClick={() => setDefaultTemplate(tpl.template_id)} />
                          <button onClick={() => deleteTemplate(tpl.template_id)} className="p-1.5 hover:bg-red-500/10 rounded" title="Supprimer"><Trash2 className="w-3 h-3 text-red-400" /></button>
                        </div>
                      </div>
                      {tpl.description && <p className="text-[10px] text-slate-500 mb-2">{tpl.description}</p>}
                      <div className="text-[10px] text-slate-500 mb-3">{tpl.blocks?.length || 0} blocs</div>
                      <Button onClick={() => applyTemplateForCampaign(tpl)} size="sm" className="w-full bg-[#3b82f6]/10 text-[#3b82f6] hover:bg-[#3b82f6]/20 text-xs">
                        <Send className="w-3 h-3 mr-1" /> Utiliser pour campagne
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── PARAMETRES TAB ── */}
          {!loading && tab === "parametres" && (
            <MailingSettings api={api} />
          )}
        </>
      )}

      {/* ── Campaign Dialog ── */}
      <Dialog open={showCampaign} onOpenChange={(v) => { setShowCampaign(v); if (!v) setEditCampaign(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editCampaign ? "Modifier la campagne" : "Nouvelle campagne mailing"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input placeholder="Nom de la campagne *" value={campForm.name} onChange={e => setCampForm(f => ({ ...f, name: e.target.value }))}
              className="bg-[#0b0811] border-white/10 text-white" data-testid="campaign-name" />
            <Input placeholder="Objet de l'email *" value={campForm.subject} onChange={e => setCampForm(f => ({ ...f, subject: e.target.value }))}
              className="bg-[#0b0811] border-white/10 text-white" data-testid="campaign-subject" />
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] text-slate-500 uppercase">Contenu</label>
                <button onClick={() => { setShowHtmlImport(true); setHtmlImport(campForm.content_html); }}
                  className="text-[10px] text-[#3b82f6] hover:underline flex items-center gap-1">
                  <Code className="w-3 h-3" /> Importer HTML
                </button>
              </div>
              <textarea placeholder="Contenu de l'email (HTML ou texte)..." value={campForm.content_html}
                onChange={e => setCampForm(f => ({ ...f, content_html: e.target.value }))}
                className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm h-32 resize-none font-mono text-xs" data-testid="campaign-content" />
            </div>
            <select value={campForm.recipients_type} onChange={e => setCampForm(f => ({ ...f, recipients_type: e.target.value, segment: e.target.value === "segment" ? (f.segment || {}) : {} }))}
              className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="campaign-recipients-type">
              <option value="all_clients">Tous les clients</option>
              <option value="segment">Segment ciblé (tags, programme, activité)</option>
            </select>
            {campForm.recipients_type === "segment" && <SegmentBuilder api={api} societeId={societeId} segment={campForm.segment} onChange={(seg) => setCampForm(f => ({ ...f, segment: seg }))} />}
            <Input type="datetime-local" value={campForm.scheduled_at} onChange={e => setCampForm(f => ({ ...f, scheduled_at: e.target.value }))}
              className="bg-[#0b0811] border-white/10 text-white" />
            <Button onClick={saveCampaign} disabled={saving || !campForm.name}
              className="w-full bg-[#3b82f6] text-white font-bold" data-testid="save-campaign-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              {editCampaign ? "Modifier" : "Creer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── HTML Import Dialog ── */}
      <Dialog open={showHtmlImport} onOpenChange={setShowHtmlImport}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>Importer du HTML</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <textarea value={htmlImport} onChange={e => setHtmlImport(e.target.value)}
              placeholder="Collez votre code HTML ici..."
              className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 font-mono text-xs h-48 resize-none" data-testid="html-import-input" />
            <Button onClick={importHtml} className="w-full bg-[#3b82f6] text-white font-bold" data-testid="confirm-html-import">
              <Upload className="w-4 h-4 mr-2" /> Importer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Template Editor Dialog (Full screen) ── */}
      <Dialog open={showEditor} onOpenChange={(v) => { setShowEditor(v); if (!v) setEditingTemplate(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Modifier le template" : "Nouveau template email"}</DialogTitle>
          </DialogHeader>

          <div className="flex gap-3 mt-2 flex-1 overflow-hidden">
            {/* Left: Block palette + block list */}
            <div className="w-2/3 flex flex-col overflow-hidden">
              {/* Template info */}
              <div className="flex gap-2 mb-3">
                <Input value={editorName} onChange={e => setEditorName(e.target.value)} placeholder="Nom du template *"
                  className="bg-[#0b0811] border-white/10 text-white text-xs flex-1" data-testid="template-name" />
                <Input value={editorDesc} onChange={e => setEditorDesc(e.target.value)} placeholder="Description"
                  className="bg-[#0b0811] border-white/10 text-white text-xs flex-1" />
              </div>

              <div className="space-y-2 mb-3">
                <AiDesignPanel api={api} societeId={societeId} onApply={applyAiDesign} />
                <ThemeEditor theme={editorTheme} onChange={setEditorTheme} />
                <Input value={editorSubject} onChange={e => setEditorSubject(e.target.value)} placeholder="Objet de l'email (utilisé pour les campagnes)" className="bg-[#0b0811] border-white/10 text-white text-xs" data-testid="template-subject" />
              </div>
              {/* Editor tabs */}
              <div className="flex gap-1 mb-3">
                <button onClick={() => setEditorTab("visual")}
                  className={`px-3 py-1.5 rounded text-xs font-medium ${editorTab === "visual" ? "bg-[#3b82f6] text-white" : "text-slate-400 hover:bg-white/5"}`}>
                  <Palette className="w-3 h-3 inline mr-1" /> Visuel
                </button>
                <button onClick={() => { setEditorTab("html"); setPreviewHtml(blocksToHtml(editorBlocks, editorTheme)); }}
                  className={`px-3 py-1.5 rounded text-xs font-medium ${editorTab === "html" ? "bg-[#3b82f6] text-white" : "text-slate-400 hover:bg-white/5"}`}>
                  <Code className="w-3 h-3 inline mr-1" /> HTML
                </button>
              </div>

              {editorTab === "visual" ? (
                <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                  {editorBlocks.map((block, idx) => (
                    <BlockEditor key={idx} block={block} index={idx}
                      onChange={updateBlock} onRemove={removeBlock}
                      onMoveUp={() => moveBlock(idx, -1)} onMoveDown={() => moveBlock(idx, 1)}
                      isFirst={idx === 0} isLast={idx === editorBlocks.length - 1} />
                  ))}
                  {editorBlocks.length === 0 && (
                    <div className="text-center py-8 text-slate-500 text-xs">
                      Ajoutez des blocs depuis le panneau de droite
                    </div>
                  )}
                </div>
              ) : (
                <textarea value={previewHtml} readOnly
                  className="flex-1 bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 font-mono text-[10px] resize-none" />
              )}
            </div>

            {/* Right: Add blocks + preview */}
            <div className="w-1/3 flex flex-col">
              <div className="text-[10px] text-slate-500 uppercase mb-2">Ajouter un bloc</div>
              <div className="space-y-1 mb-4">
                {BLOCK_TYPES.map(bt => {
                  const Icon = bt.icon;
                  return (
                    <button key={bt.type} onClick={() => addBlock(bt.type)} data-testid={`add-block-${bt.type}`}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0b0811] border border-white/5 text-xs text-slate-400 hover:border-[#3b82f6]/30 hover:text-[#3b82f6] transition-colors">
                      <Icon className="w-3.5 h-3.5" /> {bt.label}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-1 mt-auto">
                <Button onClick={previewTemplate} variant="outline" className="flex-1 border-white/10 text-slate-300 text-xs" data-testid="preview-template-btn">
                  <Eye className="w-3 h-3 mr-1" /> Apercu
                </Button>
              </div>
              <Button onClick={saveTemplate} disabled={saving || !editorName} className="w-full mt-2 bg-[#3b82f6] text-white font-bold text-xs" data-testid="save-template-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editingTemplate ? "Modifier" : "Sauvegarder"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Preview Dialog ── */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-white text-black max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-black flex items-center justify-between">Apercu du template <PreviewToggle mode={previewMode} onChange={setPreviewMode} /></DialogTitle></DialogHeader>
          <div className="mt-2 border rounded-lg overflow-hidden mx-auto transition-all" style={{ width: previewMode === "mobile" ? 390 : "100%" }} data-testid="template-preview">
            <iframe srcDoc={previewHtml} className="w-full h-[560px] border-0" title="Email preview" />
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Preset Picker Dialog ── */}
      <Dialog open={showPresetPicker} onOpenChange={setShowPresetPicker}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle>Choisir un modele de depart</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-1 mb-4">Selectionnez un template pre-construit ou partez de zero</p>
          <div className="grid grid-cols-2 gap-3" data-testid="preset-picker">
            {TEMPLATE_PRESETS.map(preset => {
              const ICON_MAP = { FileText, Zap, Heart, RotateCcw };
              const PresetIcon = ICON_MAP[preset.icon] || FileText;
              return (
                <button
                  key={preset.key}
                  onClick={() => startFromPreset(preset)}
                  className="bg-[#0b0811] border border-white/5 rounded-xl p-4 text-left hover:border-white/20 transition-all group"
                  data-testid={`preset-${preset.key}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${preset.color}15` }}>
                      <PresetIcon className="w-4 h-4" style={{ color: preset.color }} />
                    </div>
                    <span className="text-sm font-bold group-hover:text-white transition-colors">{preset.name}</span>
                  </div>
                  <p className="text-[10px] text-slate-500">{preset.description}</p>
                  <div className="text-[9px] text-slate-600 mt-2">{preset.blocks.length} blocs pre-configures</div>
                </button>
              );
            })}
          </div>
          <button
            onClick={startFromScratch}
            className="w-full mt-3 py-3 border border-dashed border-white/10 rounded-xl text-xs text-slate-400 hover:border-[#3b82f6]/30 hover:text-[#3b82f6] transition-colors"
            data-testid="preset-blank"
          >
            <Plus className="w-4 h-4 inline mr-1" /> Partir de zero
          </button>
          <button onClick={() => startFromPreset({ name: "", description: "", blocks: [] })}
            className="w-full mt-2 py-3 border border-[#f7c935]/40 bg-[#f7c935]/10 rounded-xl text-xs font-bold text-[#f7c935] hover:bg-[#f7c935]/20 transition-colors" data-testid="preset-ai">
            <Sparkles className="w-4 h-4 inline mr-1" /> Concevoir avec l'IA (base graphique promptée)
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
